import { ChangeEvent, ClipboardEvent, KeyboardEvent, MouseEvent, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
  ACCEPT_ATTRIBUTE,
  AiAttachmentPayload,
  MAX_ATTACHMENTS,
  fileToAttachment,
} from './attachmentUtils';

const BRAND_GRADIENT = 'linear-gradient(135deg, #0088CF, #00B7B2)';

type AiChatInputProps = {
  disabled?: boolean;
  disabledReason?: string;
  loading: boolean;
  onSend: (text: string, attachments: AiAttachmentPayload[]) => void;
  onStop: () => void;
};

// Unified composer capsule: attach, field, and send live inside one bordered
// surface with a focus ring — not three floating controls.
const AiChatInput = ({ disabled, disabledReason, loading, onSend, onStop }: AiChatInputProps) => {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<AiAttachmentPayload[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);

  const canSend = !disabled && (Boolean(text.trim()) || attachments.length > 0);

  const send = () => {
    if (!canSend) return;
    onSend(text, attachments);
    setText('');
    setAttachments([]);
    setAttachmentError(null);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const addFiles = async (files: File[]) => {
    setAttachmentError(null);
    for (const file of files) {
      if (attachments.length >= MAX_ATTACHMENTS) {
        setAttachmentError(`At most ${MAX_ATTACHMENTS} attachments per message`);
        break;
      }
      const result = await fileToAttachment(file);
      if (typeof result === 'string') {
        setAttachmentError(result);
      } else {
        setAttachments((current) =>
          current.length < MAX_ATTACHMENTS ? [...current, result] : current
        );
      }
    }
  };

  const onFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // allow re-picking the same file
    addFiles(files);
  };

  // Paste-to-attach (HemIQ parity): pasted images/files become attachments;
  // plain text pastes are untouched.
  const onPaste = (event: ClipboardEvent) => {
    const fileItems = Array.from(event.clipboardData?.items ?? []).filter(
      (item) => item.kind === 'file'
    );
    if (!fileItems.length) return;
    event.preventDefault();
    addFiles(fileItems.map((item) => item.getAsFile()).filter((f): f is File => f !== null));
  };

  // Clicking anywhere on the capsule focuses the field, except on the
  // attach/send/stop buttons, which keep their own actions.
  const focusInput = (event: MouseEvent<HTMLElement>) => {
    if (disabled) return;
    if ((event.target as HTMLElement).closest('button')) return;
    textInputRef.current?.focus();
  };

  const capsule = (
    <Box
      onClick={focusInput}
      sx={{
        cursor: disabled ? 'default' : 'text',
        border: '1.5px solid #e1e7ed',
        borderRadius: '18px',
        background: disabled ? '#f6f8fa' : '#fff',
        px: 1,
        py: 0.75,
        // Tall capsule with the input row pinned to the bottom edge — text
        // starts beside the icons and grows upward as it wraps.
        minHeight: 74,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:focus-within': {
          borderColor: '#0088CF',
          boxShadow: '0 0 0 3px rgba(0,136,207,0.12)',
        },
      }}
    >
      {attachments.length > 0 ? (
        <Box display="flex" gap={0.5} flexWrap="wrap" px={0.5} pt={0.25} pb={0.5}>
          {attachments.map((attachment, index) => (
            <Chip
              key={`${attachment.name}-${index}`}
              label={attachment.name}
              size="small"
              onDelete={() => setAttachments(attachments.filter((_, i) => i !== index))}
              sx={{ background: 'rgba(0,136,207,0.10)', color: '#0f7ab5' }}
            />
          ))}
        </Box>
      ) : null}
      {attachmentError ? (
        <Typography variant="caption" color="error" display="block" px={0.5} pb={0.5}>
          {attachmentError}
        </Typography>
      ) : null}
      <Box display="flex" alignItems="flex-end" gap={0.5}>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept={ACCEPT_ATTRIBUTE}
          onChange={onFilesPicked}
          aria-label="Attach files"
        />
        <Tooltip title="Attach images, PDFs, or text files">
          <span>
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || loading}
              aria-label="Attach file"
              sx={{ color: 'text.disabled', mb: 0.25, '&:hover': { color: '#0088CF' } }}
            >
              <AttachFileIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </span>
        </Tooltip>
        <InputBase
          inputRef={textInputRef}
          fullWidth
          multiline
          minRows={1}
          maxRows={8}
          placeholder={disabled ? disabledReason || 'Chat unavailable' : 'Message StackIQ…'}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          inputProps={{ 'aria-label': 'Chat message' }}
          sx={{ fontSize: 14.5, px: 0.5, py: 0.5, lineHeight: 1.5 }}
        />
        {loading ? (
          <Tooltip title="Stop generating">
            <IconButton
              onClick={onStop}
              aria-label="Stop generating"
              sx={{
                width: 36,
                height: 36,
                mb: 0.25,
                border: '1.5px solid',
                borderColor: 'text.disabled',
                color: 'text.secondary',
                position: 'relative',
                '&:hover': { borderColor: '#0088CF', color: '#0088CF', background: 'rgba(0,136,207,0.06)' },
              }}
            >
              <CircularProgress
                size={34}
                thickness={2}
                sx={{ color: 'rgba(0,136,207,0.5)', position: 'absolute', top: 0, left: 0 }}
              />
              <StopIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton
            onClick={send}
            disabled={!canSend}
            aria-label="Send"
            sx={{
              width: 36,
              height: 36,
              mb: 0.25,
              color: '#fff',
              background: BRAND_GRADIENT,
              transition: 'transform 120ms ease, opacity 150ms ease',
              '&:hover': { background: 'linear-gradient(135deg, #0077b5, #009e9a)', transform: 'scale(1.05)' },
              '&:active': { transform: 'scale(0.96)' },
              '&.Mui-disabled': { background: '#e6ebf1', color: '#b6c2cd' },
            }}
          >
            <SendIcon sx={{ fontSize: 17, ml: 0.25 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  return (
    <Box pt={1}>
      {disabled && disabledReason ? <Tooltip title={disabledReason}>{capsule}</Tooltip> : capsule}
    </Box>
  );
};

export default AiChatInput;
